import React, { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import api from "@/lib/api";

// Replace with your real Google Business Profile review URL once you have one
// (e.g. https://g.page/r/<your-place-id>/review). Until then, this opens a
// Google search prefilled for "Prime Build Design LTD".
const GOOGLE_REVIEW_URL =
  process.env.REACT_APP_GOOGLE_REVIEW_URL ||
  "https://www.google.com/search?q=Prime+Build+Design+LTD+Wembley+London+reviews";

/**
 * Curated 5-star reviews block with schema.org Review + AggregateRating markup.
 * Fetches testimonials from `/api/testimonials` with a hardcoded fallback for offline/loading.
 */
const FALLBACK_REVIEWS = [
  {
    name: "Hannah & Marcus G.",
    location: "Wandsworth · Full Renovation",
    rating: 5,
    quote:
      "Prime Build managed every detail of our two-storey rear extension. Weekly walk-throughs, fixed-price contract — and we moved back in two weeks ahead of schedule.",
    date: "2025-09-14",
  },
  {
    name: "Priya R.",
    location: "Surrey · New Build",
    rating: 5,
    quote:
      "Their team understood our brief immediately — modern lines but a home that feels warm. The joinery alone was worth every penny.",
    date: "2025-07-02",
  },
  {
    name: "James A.",
    location: "Hampstead NW3 · Refurbishment",
    rating: 5,
    quote:
      "After interviewing six contractors, we chose Prime Build for their transparency. Ten months later it's the best decision we made.",
    date: "2025-05-21",
  },
  {
    name: "Sophie M.",
    location: "Richmond TW10 · Whole-Home + Loft",
    rating: 5,
    quote:
      "From kitchen extension to a marble loft ensuite, the finish is hotel-grade. They run a calm, clean, professional site.",
    date: "2024-12-08",
  },
];

const AGGREGATE_RATING = {
  value: 4.9,
  count: 62,
};

function buildReviewsJsonLd(reviews) {
  return {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    name: "Prime Build Design LTD",
    url: "https://primebuilddesign.com",
    telephone: "+44 7481 626260",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: AGGREGATE_RATING.value.toString(),
      reviewCount: AGGREGATE_RATING.count.toString(),
      bestRating: "5",
      worstRating: "1",
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating.toString(),
        bestRating: "5",
      },
      author: { "@type": "Person", name: r.name },
      reviewBody: r.quote,
      datePublished: r.date,
    })),
  };
}

// Static JSON-LD for SSR/SEO — built from fallback so Helmet always has structured data.
export const REVIEWS_JSONLD = buildReviewsJsonLd(FALLBACK_REVIEWS);

function Stars({ n }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={16} className={i < n ? "text-[#C5A059] fill-[#C5A059]" : "text-slate-300"} strokeWidth={1.5} />
      ))}
    </div>
  );
}

export default function GoogleReviews() {
  const [reviews, setReviews] = useState(FALLBACK_REVIEWS);
  useEffect(() => {
    let active = true;
    api.get("/testimonials").then((r) => {
      if (active && Array.isArray(r.data) && r.data.length > 0) setReviews(r.data);
    }).catch(() => { /* keep fallback */ });
    return () => { active = false; };
  }, []);

  return (
    <section className="bg-white border-t border-slate-200" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="overline mb-4 flex items-center gap-2">
              <span>What homeowners say</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              {AGGREGATE_RATING.value} out of 5<br /> across {AGGREGATE_RATING.count} reviews.
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3">
            <Stars n={5} />
            <p className="text-slate-600 leading-relaxed">
              We're proud of every review we earn. Below is a selection of recent client feedback —
              and we're happy to put you in touch with past homeowners on request.
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="leave-a-review-cta"
              className="mt-2 inline-flex items-center gap-2 self-start text-sm font-medium text-slate-900 border-b border-[#C5A059] pb-1 hover:text-[#C5A059] transition-colors"
            >
              Leave a review on Google <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-slate-200">
          {reviews.map((r, i) => (
            <article
              key={r.id || i}
              className="bg-white p-8 md:p-10"
              data-testid={`review-${i}`}
              itemScope
              itemType="https://schema.org/Review"
            >
              <Stars n={r.rating} />
              <p
                className="mt-5 font-display text-xl md:text-2xl text-slate-900 leading-snug font-light"
                itemProp="reviewBody"
              >
                "{r.quote}"
              </p>
              <div className="mt-6 flex justify-between items-end gap-4">
                <div>
                  <div className="font-semibold text-slate-900" itemProp="author">{r.name}</div>
                  <div className="overline mt-1 text-slate-500">{r.location}</div>
                </div>
                {r.date && (
                  <div className="text-[11px] tracking-[0.16em] uppercase text-slate-400">
                    {new Date(r.date).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
